import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Users, Car, MapPin, TrendingUp, Trash2, Search, X, UserPlus, Ban, CheckCircle, XCircle, Clock, Shield, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allRides, setAllRides] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setAllUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchAllRides = async () => {
    try {
      const response = await axios.get(`${API}/admin/rides`);
      setAllRides(response.data);
    } catch (error) {
      toast.error('Failed to fetch rides');
    }
  };

  const fetchPendingDrivers = async () => {
    try {
      const response = await axios.get(`${API}/admin/drivers/pending`);
      setPendingDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch pending drivers:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAllUsers();
    fetchAllRides();
    fetchPendingDrivers();
  }, []);

  const handleApproveDriver = async (driverId) => {
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/approve`);
      toast.success('Driver approved successfully');
      fetchPendingDrivers();
      fetchAllUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve driver');
    }
  };

  const handleRejectDriver = async () => {
    if (!selectedDriver) return;
    try {
      await axios.post(`${API}/admin/drivers/${selectedDriver.id}/reject?reason=${encodeURIComponent(rejectReason || 'Application did not meet requirements')}`);
      toast.success('Driver rejected');
      setShowRejectModal(false);
      setSelectedDriver(null);
      setRejectReason('');
      fetchPendingDrivers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject driver');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully');
      fetchAllUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleAdminCancelRide = async () => {
    if (!selectedRide || !cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    try {
      await axios.post(`${API}/admin/rides/${selectedRide.id}/cancel?reason=${encodeURIComponent(cancelReason)}`);
      toast.success('Ride cancelled successfully');
      setShowCancelModal(false);
      setSelectedRide(null);
      setCancelReason('');
      fetchAllRides();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const handleAdminAssignDriver = async () => {
    if (!selectedRide || !selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }
    try {
      await axios.post(`${API}/admin/rides/${selectedRide.id}/assign?driver_id=${selectedDriverId}`);
      toast.success('Driver assigned successfully');
      setShowAssignModal(false);
      setSelectedRide(null);
      setSelectedDriverId('');
      fetchAllRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    }
  };

  const availableDrivers = allUsers.filter(u => u.role === 'driver');

  const getStatusColor = (status) => {
    const colors = {
      requested: 'bg-yellow-500/20 text-yellow-400',
      accepted: 'bg-blue-500/20 text-blue-400',
      en_route: 'bg-purple-500/20 text-purple-400',
      arrived: 'bg-green-500/20 text-green-400',
      in_progress: 'bg-cyan-500/20 text-neon-cyan',
      completed: 'bg-green-600/20 text-green-500',
      cancelled: 'bg-red-500/20 text-red-400',
    };
    return colors[status] || 'bg-white/10 text-white';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRides = allRides.filter(r => {
    const term = searchTerm.toLowerCase();
    return (
      r.serial_number?.toLowerCase().includes(term) ||
      r.rider_name?.toLowerCase().includes(term) ||
      r.driver_name?.toLowerCase().includes(term) ||
      r.pickup_address?.toLowerCase().includes(term) ||
      r.dropoff_address?.toLowerCase().includes(term) ||
      r.rider_email?.toLowerCase().includes(term) ||
      r.driver_email?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-surface">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple flex items-center justify-center">
                <span className="text-xl font-bold">A</span>
              </div>
              <div>
                <h1 className="font-outfit font-bold text-2xl">Admin Dashboard</h1>
                <p className="text-sm text-white/60">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="mailto:customer-support@vgoride.net"
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/60 hover:text-neon-cyan transition-colors"
                data-testid="admin-contact-link"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">customer-support@vgoride.net</span>
              </a>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            {['overview', 'users', 'rides', 'approvals'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 font-medium capitalize transition-colors border-b-2 flex items-center gap-2 ${
                  activeTab === tab
                    ? 'border-neon-cyan text-neon-cyan'
                    : 'border-transparent text-white/60 hover:text-white'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab}
                {tab === 'approvals' && pendingDrivers.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingDrivers.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <h2 className="font-outfit font-bold text-3xl mb-8">Platform Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5" data-testid="stat-users">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-white/40" />
                </div>
                <div className="font-mono text-3xl font-bold mb-1">{stats.total_users}</div>
                <div className="text-white/60 text-sm">Total Riders</div>
              </div>

              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5" data-testid="stat-drivers">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Car className="w-6 h-6 text-purple-400" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-white/40" />
                </div>
                <div className="font-mono text-3xl font-bold mb-1">{stats.total_drivers}</div>
                <div className="text-white/60 text-sm">Total Drivers</div>
              </div>

              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5" data-testid="stat-rides">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-neon-cyan" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-white/40" />
                </div>
                <div className="font-mono text-3xl font-bold mb-1">{stats.total_rides}</div>
                <div className="text-white/60 text-sm">Total Rides</div>
              </div>

              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5" data-testid="stat-active">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="text-xs font-medium text-green-400">LIVE</div>
                </div>
                <div className="font-mono text-3xl font-bold mb-1">{stats.active_rides}</div>
                <div className="text-white/60 text-sm">Active Rides</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
                <h3 className="font-outfit font-bold text-xl mb-4">Today&apos;s Performance</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Completed Rides</span>
                    <span className="font-mono font-bold text-neon-cyan">{stats.completed_today}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Completion Rate</span>
                    <span className="font-mono font-bold">
                      {stats.total_rides > 0 ? ((stats.completed_today / stats.total_rides) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-dark-surface rounded-2xl p-6 border border-white/5">
                <h3 className="font-outfit font-bold text-xl mb-4">Platform Health</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Active Drivers</span>
                    <span className="font-mono font-bold text-green-400">
                      {allUsers.filter(u => u.role === 'driver' && u.available).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Driver Availability</span>
                    <span className="font-mono font-bold">
                      {stats.total_drivers > 0
                        ? ((allUsers.filter(u => u.role === 'driver' && u.available).length / stats.total_drivers) * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-outfit font-bold text-3xl">User Management</h2>
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-dark-surface border-white/10 focus:border-neon-cyan rounded-xl"
                  data-testid="search-users-input"
                />
              </div>
            </div>

            <div className="bg-dark-surface rounded-2xl border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-dark-bg border-b border-white/10">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Role</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-white/60">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredUsers.map((u, idx) => (
                      <tr key={u.id} className="hover:bg-white/5 transition-colors" data-testid={`user-row-${idx}`}>
                        <td className="px-6 py-4 font-medium">{u.name}</td>
                        <td className="px-6 py-4 text-white/60">{u.email}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            u.role === 'driver' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-white/60 font-mono text-sm">{u.phone || '-'}</td>
                        <td className="px-6 py-4">
                          {u.role === 'driver' && (
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              u.available ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/60'
                            }`}>
                              {u.available ? 'Online' : 'Offline'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                            data-testid={`delete-user-btn-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Rides Tab */}
        {activeTab === 'rides' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-outfit font-bold text-3xl">Ride Management</h2>
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="text"
                  placeholder="Search by serial #, rider, driver..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-dark-surface border-white/10 focus:border-neon-cyan rounded-xl"
                  data-testid="search-rides-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              {filteredRides.map((ride, idx) => (
                <div
                  key={ride.id}
                  className="bg-dark-surface rounded-2xl p-6 border border-white/5"
                  data-testid={`ride-card-${idx}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      {/* Serial Number - Primary identifier for tracking */}
                      {ride.serial_number && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-white/40">Tracking #:</span>
                          <span className="font-mono text-sm font-bold text-neon-cyan bg-neon-cyan/10 px-2 py-0.5 rounded">
                            {ride.serial_number}
                          </span>
                        </div>
                      )}
                      <div className="font-mono text-xs text-white/30 mb-1">ID: {ride.id}</div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ride.status)}`}>
                          {formatStatus(ride.status)}
                        </span>
                        <span className="text-sm text-white/60">
                          {new Date(ride.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-white/60 mb-1">Fare</div>
                      <div className="text-2xl font-bold text-neon-cyan">${ride.fare}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm text-white/60 mb-3">Trip Details</div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-neon-cyan mt-1 flex-shrink-0" />
                          <div className="text-sm">{ride.pickup_address}</div>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-neon-purple mt-1 flex-shrink-0" />
                          <div className="text-sm">{ride.dropoff_address}</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-white/60 mb-3">Participants</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/60">Rider:</span>
                          <span className="text-sm font-medium">{ride.rider_name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/60">Driver:</span>
                          <span className="text-sm font-medium">{ride.driver_name || 'Unassigned'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                    <div>
                      <div className="text-xs text-white/60 mb-1">Distance</div>
                      <div className="font-mono font-medium">{ride.distance.toFixed(1)} miles</div>
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-1">ETA</div>
                      <div className="font-mono font-medium">{ride.eta} min</div>
                    </div>
                    <div>
                      <div className="text-xs text-white/60 mb-1">Created</div>
                      <div className="font-mono font-medium text-xs">
                        {new Date(ride.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Admin Action Buttons */}
                  {ride.status !== 'completed' && ride.status !== 'cancelled' && (
                    <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
                      {/* Assign Driver Button - Only show for rides without a driver or in requested status */}
                      {(ride.status === 'requested' || !ride.driver_id) && ride.rider_id && (
                        <button
                          onClick={() => {
                            setSelectedRide(ride);
                            setShowAssignModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan/30 rounded-lg transition-colors"
                          data-testid={`assign-driver-btn-${idx}`}
                        >
                          <UserPlus className="w-4 h-4" />
                          Assign Driver
                        </button>
                      )}
                      
                      {/* Cancel Ride Button */}
                      <button
                        onClick={() => {
                          setSelectedRide(ride);
                          setShowCancelModal(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
                        data-testid={`cancel-ride-btn-${idx}`}
                      >
                        <Ban className="w-4 h-4" />
                        Cancel Ride
                      </button>
                    </div>
                  )}

                  {/* Show cancellation info if cancelled */}
                  {ride.status === 'cancelled' && ride.cancellation_reason && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="text-xs text-white/60 mb-1">Cancellation Reason</div>
                      <div className="text-sm text-red-400">{ride.cancellation_reason}</div>
                      {ride.cancelled_by && (
                        <div className="text-xs text-white/40 mt-1">Cancelled by: {ride.cancelled_by}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Driver Approvals Tab */}
        {activeTab === 'approvals' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-outfit font-bold text-3xl">Driver Approvals</h2>
                <p className="text-white/60 mt-1">Review and approve pending driver applications</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 rounded-xl">
                <Clock className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-medium">{pendingDrivers.length} pending</span>
              </div>
            </div>

            {pendingDrivers.length === 0 ? (
              <div className="text-center py-20">
                <Shield className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="font-outfit font-bold text-2xl mb-2">No Pending Approvals</h3>
                <p className="text-white/60">All driver applications have been reviewed.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingDrivers.map((driver, idx) => (
                  <div
                    key={driver.id}
                    className="bg-dark-surface rounded-2xl p-6 border border-white/5"
                    data-testid={`pending-driver-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-purple-400 font-bold text-lg">
                            {driver.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">{driver.name}</div>
                          <div className="text-sm text-white/60">@{driver.username}</div>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">
                        Pending
                      </span>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Email</span>
                        <span className="text-white truncate ml-2">{driver.email}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Phone</span>
                        <span className="text-white font-mono">{driver.phone}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Vehicle</span>
                        <span className="text-white">{driver.vehicle_type || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Plate</span>
                        <span className="text-white font-mono">{driver.vehicle_plate || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">License</span>
                        <span className="text-white font-mono">{driver.license_number || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Registered</span>
                        <span className="text-white/80">
                          {new Date(driver.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSelectedDriver(driver);
                          setShowRejectModal(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl transition-colors"
                        data-testid={`reject-driver-btn-${idx}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApproveDriver(driver.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-xl transition-colors"
                        data-testid={`approve-driver-btn-${idx}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Ride Modal */}
      {showCancelModal && selectedRide && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-dark-surface rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-bold text-xl">Cancel Ride</h2>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedRide(null);
                  setCancelReason('');
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-dark-bg rounded-xl">
              <div className="text-sm text-white/60 mb-2">Ride Details</div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Rider:</span> {selectedRide.rider_name || 'N/A'}
              </div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Driver:</span> {selectedRide.driver_name || 'Unassigned'}
              </div>
              <div className="text-sm">
                <span className="text-white/60">Status:</span>{' '}
                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(selectedRide.status)}`}>
                  {formatStatus(selectedRide.status)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-2 text-white/80">Reason for cancellation *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason for cancelling this ride..."
                className="w-full bg-dark-bg border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                rows="3"
                data-testid="admin-cancel-reason-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedRide(null);
                  setCancelReason('');
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors"
              >
                Keep Ride
              </button>
              <button
                onClick={handleAdminCancelRide}
                disabled={!cancelReason.trim()}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-full font-bold py-3 transition-all"
                data-testid="admin-confirm-cancel-btn"
              >
                Cancel Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {showAssignModal && selectedRide && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-dark-surface rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-bold text-xl">Assign Driver</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedRide(null);
                  setSelectedDriverId('');
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-dark-bg rounded-xl">
              <div className="text-sm text-white/60 mb-2">Ride Details</div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Rider:</span> {selectedRide.rider_name || 'N/A'}
              </div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Pickup:</span> {selectedRide.pickup_address}
              </div>
              <div className="text-sm">
                <span className="text-white/60">Fare:</span>{' '}
                <span className="text-neon-cyan font-bold">${selectedRide.fare}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-2 text-white/80">Select Driver *</label>
              {availableDrivers.length === 0 ? (
                <div className="text-center py-4 text-white/60">No drivers available</div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableDrivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => setSelectedDriverId(driver.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                        selectedDriverId === driver.id
                          ? 'bg-neon-cyan/20 border-2 border-neon-cyan'
                          : 'bg-dark-bg border-2 border-transparent hover:border-white/20'
                      }`}
                      data-testid={`select-driver-${driver.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-purple-400 font-bold">
                            {driver.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="text-left">
                          <div className="font-medium">{driver.name}</div>
                          <div className="text-xs text-white/60">{driver.email}</div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        driver.available ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/60'
                      }`}>
                        {driver.available ? 'Online' : 'Offline'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedRide(null);
                  setSelectedDriverId('');
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdminAssignDriver}
                disabled={!selectedDriverId}
                className="flex-1 bg-neon-cyan text-black hover:bg-[#00B8D4] disabled:opacity-50 disabled:cursor-not-allowed rounded-full font-bold py-3 transition-all"
                data-testid="admin-confirm-assign-btn"
              >
                Assign Driver
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Driver Modal */}
      {showRejectModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
          <div className="bg-dark-surface rounded-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit font-bold text-xl">Reject Driver</h2>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedDriver(null);
                  setRejectReason('');
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-dark-bg rounded-xl">
              <div className="text-sm text-white/60 mb-2">Driver Details</div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Name:</span> {selectedDriver.name}
              </div>
              <div className="text-sm mb-1">
                <span className="text-white/60">Email:</span> {selectedDriver.email}
              </div>
              <div className="text-sm">
                <span className="text-white/60">Vehicle:</span> {selectedDriver.vehicle_type} ({selectedDriver.vehicle_plate})
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-2 text-white/80">Reason for rejection (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejecting this driver..."
                className="w-full bg-dark-bg border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                rows="3"
                data-testid="reject-reason-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedDriver(null);
                  setRejectReason('');
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectDriver}
                className="flex-1 bg-red-500 hover:bg-red-600 rounded-full font-bold py-3 transition-all"
                data-testid="confirm-reject-btn"
              >
                Reject Driver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}